import { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import InputMask from "react-input-mask";
import {
    FormControl,
    FormHelperText,
    TextField,
} from "@mui/material";
import toast from "react-hot-toast";

import PostcodeSearch from "./personalInformation/PostcodeSearch";
import personalInfoSchema from "../schema/personalInfo";
import InputField from "../../common/InputField";
import { Context } from "../Provider";
import inputFieldSize from "./accountCreated/inputFieldSize";
import {
    bankDetails,
    businessDetails,
    inputFieldsAddresses,
    inputFieldsLicenceAndVehicleDetails,
    nextOfKinDetails,
    taxAndInsuranceDetails,
} from "./personalInformation/inputFields";
import { theme } from "../../theme";
import BtnSpinner from "../../common/BtnSpinner";
import { updatePersonalInformation } from "../service";
import PaymentTerms from "./personalInformation/PaymentTerms";
import CardTemplate from "../../common/CardTemplate";
import InputRequired from "../../common/InputRequired";
import useIsMobile from "../../common/hooks/useIsMobile";
import VehicleBrands from "./personalInformation/VehicleBrand";
import VehicleModels from "./personalInformation/VehicleModels";
import VehicleColors from "./personalInformation/VehicleColors";

const personalInformation = [
    "ftm_bank_account_number",
    "ftm_bank_name",
    "ftm_bank_sort_code",
    "ftm_place",
    "ftm_driving_license_number",
    "ftm_ni_number",
    "ftm_utr_number",
    "ftm_postcode",
    "ftm_street_address"
];

const FormSection = ({ title, children }) => (
    <>
        <h6 className="mt-4">{title}</h6>
        <div className="mb-3" style={{ borderTop: "1px solid #bfbdbd" }} />
        {children}
    </>
);

const MaskedInput = ({ name, control, placeholder, mask, error, label, readOnly = false }) => (
    <FormControl error={!!error} fullWidth>
        <strong>
            {label}
            <InputRequired />
        </strong>
        <Controller
            name={name}
            control={control}
            render={({ field }) => (
                <InputMask
                    {...field}
                    mask={mask}
                // maskChar=""
                >
                    {(inputProps) => (
                        <TextField
                            {...inputProps}
                            placeholder={placeholder}
                            size="small"
                            type="text"
                            error={!!error}
                            InputProps={{
                                readOnly: readOnly,
                            }}
                            sx={{
                                '& .MuiInputBase-root': {
                                    backgroundColor: readOnly ? '#f5f5f5' : 'inherit', // light gray when readonly
                                },
                            }}
                        />
                    )}
                </InputMask>
            )}
        />
        {error && <FormHelperText>{error.message}</FormHelperText>}
    </FormControl>
);

const PersonalInformation = () => {
    const { state } = useContext(Context);
    const { ftm, step, commonSettings, isCompletedRegistration } = state;

    const [loading, setLoading] = useState(false);
    const [openPaymentSettinsModal, setOpenPaymentSettinsModal] = useState(false);
    const [vehicleColor, setVehicleColor] = useState("");
    const [vehicleBrand, setVehicleBrand] = useState("");

    const isMobile = useIsMobile();

    const schema = personalInfoSchema(
        commonSettings?.phone_number_format || "+44 7XX XXX XXXX",
        ftm?.ftm_contact_number?.replace(/\s+/g, '') // normalized phone number to block
    );

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        setValue
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: ftm,
    });

    const readOnly = isCompletedRegistration;
    const showSubmitButton = !isCompletedRegistration;

    const handleOpenPaymentSettinsModal = () => setOpenPaymentSettinsModal(true);
    const handleClosePaymentSettinsModal = () => setOpenPaymentSettinsModal(false);

    useEffect(() => {
        const checkSubmitPersonalDetails = () => {
            return personalInformation.every(field => {
                const value = ftm[field];
                return value !== null && value !== undefined && String(value).trim() !== '';
            });
        };

        const isSubmitPersonalDetails = checkSubmitPersonalDetails();

        if (isSubmitPersonalDetails && step?.stepId === 4) {
            setOpenPaymentSettinsModal(true);
        }
    }, [ftm, step?.stepId]);

    const onSubmit = async (data) => {
        try {
            setLoading(true);
            const response = await updatePersonalInformation({ ftm_id: ftm?.ftm_id, ...data });
            if (response.status === "Success") {
                handleOpenPaymentSettinsModal();

                toast.success(response.message);
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            console.error("Registration failed:", error);
            toast.error("Registration failed.");
        } finally {
            setLoading(false);
        }
    };

    const renderInputFields = (fields, className = "col-md-6", notRequired = false, tooltip = null) =>
        fields.map(({ name, label, placeholder, type, hidden }, idx) => (
            <div className={`col-12 ${className}`} key={idx}>
                <InputField
                    field={{ name, label, placeholder, size: inputFieldSize, type }}
                    register={register}
                    errors={errors}
                    readOnly={readOnly}
                    hidden={hidden}
                    notRequired={notRequired}
                    tooltip={tooltip}
                />
            </div>
        ));

    return (
        <CardTemplate
            stepId={5}
            currentStep={step?.stepId}
            body={
                <>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="row">
                            <div className="col-12">

                                <FormSection title="Address">
                                    <PostcodeSearch setValue={setValue} />
                                    <div className="row mt-3">
                                        {renderInputFields(inputFieldsAddresses, "col-md-4")}
                                    </div>
                                </FormSection>

                                <FormSection title="Licence & Vehicle Details">
                                    <div className="row">
                                        {renderInputFields(inputFieldsLicenceAndVehicleDetails, "col-md-6")}
                                        <div className="col-12 col-md-6">
                                            <FormControl error={!!errors.ftm_vehicle_number} fullWidth>
                                                <strong>
                                                    Vehicle registration
                                                    <InputRequired />
                                                </strong>
                                                <Controller
                                                    name="ftm_vehicle_number"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            placeholder="Ex:- AB12 XYZ"
                                                            size="small"
                                                            type="text"
                                                            error={!!errors.ftm_vehicle_number}
                                                            InputProps={{
                                                                readOnly: readOnly,
                                                            }}
                                                            sx={{
                                                                '& .MuiInputBase-root': {
                                                                    backgroundColor: readOnly ? '#f5f5f5' : 'inherit', // light gray when readonly
                                                                },
                                                            }}
                                                            inputProps={{
                                                                style: { textTransform: 'uppercase' } // Visual enforcement
                                                            }}
                                                        />
                                                    )}
                                                />
                                                {errors.ftm_vehicle_number && (
                                                    <FormHelperText>{errors.ftm_vehicle_number.message}</FormHelperText>
                                                )}
                                            </FormControl>
                                        </div>
                                    </div>
                                    <div className="row mt-2">
                                        <div className="col-12 col-md-4">
                                            <VehicleColors setValue={setValue} errors={errors}/>
                                        </div>

                                        <div className="col-12 col-md-4">
                                            <VehicleBrands setValue={setValue} setVehicleBrand={setVehicleBrand} errors={errors}/>
                                        </div>

                                        <div className="col-12 col-md-4">
                                            <VehicleModels brand={vehicleBrand} setValue={setValue} errors={errors}/>
                                        </div>
                                    </div>
                                </FormSection>

                                <FormSection title="Emergency Contact">
                                    <div className="row">
                                        {renderInputFields(nextOfKinDetails, "col-md-6", false, "We like to have emergency contact details in case of an emergency on site")}
                                        <div className="col-12 col-md-6">
                                            <MaskedInput
                                                name="ftm_next_of_kin_contact_number"
                                                control={control}
                                                placeholder="Phone number"
                                                mask={commonSettings?.phone_number_format || "+44 799 999 9999"}
                                                error={errors.ftm_next_of_kin_contact_number}
                                                label="Emergency contact number"
                                                readOnly={readOnly}
                                            />
                                        </div>

                                        <p className="mt-2 mb-0">
                                            <strong>Note:</strong> If you change your mind then you can change this at later.
                                        </p>
                                    </div>
                                </FormSection>

                                <FormSection title="Tax & Insurance Details">
                                    <div className="row">
                                        {renderInputFields(taxAndInsuranceDetails, "col-md-6")}
                                    </div>
                                </FormSection>

                                <FormSection title="Your Business Details (If Applicable)">
                                    <div className="row">
                                        {renderInputFields(businessDetails, "col-md-4", true)}
                                    </div>
                                </FormSection>

                                <FormSection title="Your Bank Details">
                                    <div className="row">
                                        {renderInputFields(bankDetails, "col-md-4")}
                                        <div className="col-12 col-md-4">
                                            <MaskedInput
                                                name="ftm_bank_sort_code"
                                                control={control}
                                                placeholder="Bank sort code"
                                                mask="99-99-99"
                                                error={errors.ftm_bank_sort_code}
                                                label="Bank Sort Code"
                                                readOnly={readOnly}
                                            />
                                        </div>
                                        <div className="col-12 col-md-4">
                                            <MaskedInput
                                                name="ftm_bank_account_number"
                                                control={control}
                                                placeholder="Bank account number"
                                                mask="9999-9999"
                                                error={errors.ftm_bank_account_number}
                                                label="Bank Account Number"
                                                readOnly={readOnly}
                                            />
                                        </div>
                                    </div>
                                </FormSection>

                                {showSubmitButton && (
                                    <div className="mt-5 d-flex align-items-center justify-content-end">
                                        <button
                                            className={`btn bg-gradient-${theme.main} mb-0 ms-2 btn-sm ${isMobile && 'w-100'}`}
                                            type="submit"
                                            disabled={loading}
                                        >
                                            {loading ? <BtnSpinner /> : "PROCEED"}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </form>

                    <PaymentTerms show={openPaymentSettinsModal} handleClose={handleClosePaymentSettinsModal} />
                </>
            }
        />
    );
};

export default PersonalInformation;